import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/lib/api';
import { refreshContent } from '@/lib/content-sync';
import { currentLocale } from '@/lib/i18n';

export type ContentLocale = 'he' | 'en';

const entrySchema = z.object({
  id: z.string(),
  locale: z.string(),
  namespace: z.string(),
  key: z.string(),
  value: z.string(),
  updatedAt: z.string(),
});
export type ContentEntry = z.infer<typeof entrySchema>;

const ROOT = ['platform-admin', 'content'] as const;

export function useContentOverrides(locale: ContentLocale) {
  return useQuery({
    queryKey: [...ROOT, locale],
    queryFn: ({ signal }) =>
      request(`/platform-admin/content?locale=${locale}`, { schema: z.array(entrySchema), signal }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** אחרי שמירה — גם הממשק הפתוח מתעדכן מיד, אם העריכה בשפה שלו. */
function useAfterWrite() {
  const qc = useQueryClient();
  return async (locale: ContentLocale) => {
    await qc.invalidateQueries({ queryKey: [...ROOT, locale] });
    if (locale === currentLocale()) await refreshContent(locale);
  };
}

export function useSaveContent() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (input: { locale: ContentLocale; namespace: string; key: string; value: string }) =>
      request('/platform-admin/content', { method: 'PUT', body: input, schema: entrySchema }),
    onSuccess: (_d, v) => after(v.locale),
  });
}

export function useRevertContent() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (input: { id: string; locale: ContentLocale }) =>
      request(`/platform-admin/content/${encodeURIComponent(input.id)}`, { method: 'DELETE' }),
    onSuccess: (_d, v) => after(v.locale),
  });
}
